import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { chatActions, galleryActions } from "../../../di/di";
import { FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  User,
  Image,
  ChevronRight,
  Search,
  ArrowLeft,
  Grid,
  List,
  Calendar,
  Heart,
  Lock,
  Download,
  Plus,
  MessageCircle,
  X,
  ChevronLeft,
} from "lucide-react";
import profile from "../../modelprofile/utils/profile_data";
import { GetImageStatus, getInitialImages, getMoreImages, GetMoreImageStatus } from "../state/gallery_slice";
import { Virtuoso, VirtuosoGrid } from "react-virtuoso";
import "../style/chat_gallery_image_container.css";
import { StringUtils } from "../../../../../core/utils/string_utils";
import ReactDOM from "react-dom";
import { keyframes } from "framer-motion";
import { set } from "animejs";
import { ModelProfileRoute } from "../../../../../core/routes/route_constants";

const ChatGalleryComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const virtuosoRef = useRef(null);
  const bottomRef = useRef(null);

  const mediaItems = useSelector((state) => state.gallery.images);
  const imagesCount = useSelector((state) => state.gallery.imagesCount);
  const getImageStatus = useSelector((state) => state.gallery.getImageStatus);
  const getMoreImageStatus = useSelector((state) => state.gallery.getMoreImageStatus);
  const showGallery = useSelector((state) => state.chat.showGallery);
  const user = useSelector((state) => state.user.user);
  const avatarProfile = useSelector((state) => state.modelProfile.profile);

  // Function to handle going back to chat
  const handleBackToChat = () => {
    dispatch(chatActions.setShowGallery(false));
  };

  // Function to handle viewing the profile
  const handleViewProfile = () => {
    navigate(`${ModelProfileRoute.replace(":name", profile.name.toLowerCase())}`);
  };

  // Open image in fullscreen
  const openFullscreen = (imageItem) => {
    /// Show Subscription Modal
    if (!user.tierId && imageItem.isBlur) {
      dispatch(chatActions.setShowSubscriptionModal(true));
    } else {
      dispatch(galleryActions.showPhotoViewer(imageItem));
    }
  };

  // When the UI Loads, fetch the initial images
  useEffect(() => {
    if (getImageStatus !== GetImageStatus.ongoing && getImageStatus !== GetImageStatus.noImages) {
      dispatch(getInitialImages());
    }
  }, [dispatch]);

  if (!showGallery) return null;

  return (
    <div
      className="inset-0
                bg-gradient-to-b bg-gradient-to-b from-[#0d0c22] to-[#1a1333] backdrop-blur-md 
                text-white
                min-w-[28rem] max-md:min-w-full h-screen
                overflow-y-hidden
                animate-fade-in"
    >
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: "100%", width: "100%" }}
        data={[0]}
        atBottomStateChange={(e) => {
          if (e) {
            if (getMoreImageStatus !== GetMoreImageStatus.ongoing && getMoreImageStatus !== GetMoreImageStatus.noMoreImages) {
              dispatch(getMoreImages());
            }
          }
        }}
        onScroll={(e) => {
          const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
          if (bottom) {
            console.log("Is Bottom");
          }
        }}
        components={{
          Header: () => (
            <div className="flex flex-col w-full pt-4 pr-4 pl-4">
              {/* Header with back button and close button */}
              <div className="flex justify-between md:justify-end items-center ">
                <button className="flex items-center text-white/80 hover:text-white transition-colors md:hidden" onClick={handleBackToChat}>
                  <ArrowLeft size={18} className="mr-1" />
                  <span>Back to chat</span>
                </button>
                <button
                  className="w-8 h-8 bg-red-900/60 rounded-full flex items-center justify-center border border-red-500/30 text-white/80 hover:text-white transition-colors max-md:hidden active:translate-y-0.5 active:shadow-none"
                  onClick={handleBackToChat}
                >
                  <FiX size={18} />
                </button>
              </div>

              {/* Profile header with glow effect */}
              <div className="flex flex-col items-center">
                <div className="relative mb-2">
                  <div className="absolute inset-0 bg-red-500  rounded-full blur-lg opacity-20"></div>
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#d13939] to-[#8f1e1e] flex items-center justify-center border-2 border-red-400/30 relative">
                    <Heart size={40} className="absolute text-pink-200" />
                    {(avatarProfile?.profileUrl ?? "") !== "" && (
                      <div
                        className="absolute rounded-full w-[5.58rem] h-[5.58rem]"
                        style={{ background: `url('${avatarProfile?.profileUrl}') top / cover`, backgroundRepeat: "no-repeat" }}
                      />
                    )}
                  </div>
                </div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-red-300 to-pink-300 bg-clip-text text-transparent">
                  {avatarProfile?.name ?? ""}
                </h1>
                <p className="text-sm text-gray-400 mt-3 mb-6">{mediaItems.length > 0 ? `${imagesCount} photos` : "No photos yet"}</p>
              </div>

              {/* View profile button */}
              <div className="flex justify-center mb-8">
                <div
                  className="flex flex-col items-center cursor-pointer group active:translate-y-0.5 active:shadow-none transition-transform"
                  onClick={handleViewProfile}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#d13939] to-[#8f1e1e] rounded-full flex items-center justify-center mb-1 border border-red-400/30 shadow-md shadow-red-900/20 group-hover:scale-105">
                    <User size={24} className="text-pink-100" />
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">View profile</span>
                </div>
              </div>

              {/* Media header */}
              <div className="text-center py-3 border-t border-b border-red-900/40 mb-6">
                <span className="text-lg font-medium bg-gradient-to-r from-red-300 to-pink-300 bg-clip-text text-transparent">Media</span>
              </div>
            </div>
          ),
        }}
        itemContent={(index, item) => (
          <>
            {mediaItems.length > 0 ? (
              <div className="w-full">
                <div className="pb-4 pr-4 pl-4 grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center">
                  {/* Gallery grid */}
                  {mediaItems.length > 0 &&
                    mediaItems.map((item) => (
                      <div
                        key={`${item.id}-${StringUtils.generateRandomCharacters()}}`}
                        className={`
                          gallery-image-item-container
                          transform transition-transform duration-300
                          hover:scale-105
                          sm:w-48 sm:h-48
                          md:w-40 md:h-40 
                          lg:w-32 lg:h-32
                          rounded-lg overflow-hidden relative group transition-all duration-500 transform animate-fade-scale-in cursor-pointer`}
                        style={{
                          animationDelay: `${mediaItems.indexOf(item) * 100}ms`,
                        }}
                        onClick={() => openFullscreen(item)}
                      >
                        {/* Background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900"></div>

                        {/* Placeholder image icon */}
                        <div className="flex items-center justify-center h-full relative">
                          <Image size={32} className="text-gray-700 absolute" />
                          <img src={item.imageUrl} alt=" " className="img-cover rounded-lg absolute" />
                        </div>

                        {/* Exclusive */}
                        {item.isBlur && (
                          <div className="absolute top-0 left-0 bottom-0 right-0 flex flex-col items-center justify-center space-x-1 w-full bg-black bg-opacity-30">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-800 to-red-900 flex items-center justify-center mb-3 shadow-lg shadow-red-900/40">
                              <Heart size={16} className="text-pink-200" />
                            </div>
                            <div className="bg-gradient-to-r from-red-800 to-red-900 rounded-full px-4 py-1 text-white font-medium text-xs shadow-lg shadow-red-900/30">
                              Exclusive
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="relative w-16 h-16 mb-4">
                  <div className="absolute inset-0 bg-red-500 rounded-full blur-md opacity-10 animate-pulse"></div>
                  <div className="relative z-10 w-full h-full flex items-center justify-center">
                    {/* Layered icons to create a "locked album" effect */}
                    <div className="absolute w-12 h-10 bg-gradient-to-br from-[#d13939]/80 to-[#8f1e1e]/80 rounded-lg transform rotate-6 border border-red-400/20"></div>
                    <div className="absolute w-12 h-10 bg-gradient-to-br from-[#d13939]/90 to-[#8f1e1e]/90 rounded-lg transform -rotate-6 border border-red-400/20"></div>
                    <div className="absolute w-12 h-10 bg-gradient-to-br from-[#d13939] to-[#8f1e1e] rounded-lg flex items-center justify-center border border-red-400/30">
                      <Heart size={18} className="text-pink-200" />
                    </div>
                    <div className="absolute top-[-5px] right-2">
                      <div className="w-6 h-6 bg-[#8f1e1e] rounded-full flex items-center justify-center border border-red-400/30 shadow-md">
                        <Lock size={12} className="text-white" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-gray-300 text-sm">Nothing Here… Yet</p>
                </div>
                <p className="text-sm text-gray-400 mt-3 px-10 text-center">Invite Eve to share an image and it will show up in this secure space.</p>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};

export default ChatGalleryComponent;
